import { useEffect, useState } from 'react';
import { makeStyles, tokens, Body1, Caption1, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useAuth } from '../auth/TeamsAuthProvider';
import { Logo } from './Logo';

/**
 * Shared loading primitive (spec §4.6). Replaces the four loading idioms
 * (bare `Spinner`, ad-hoc `Skeleton`/`SkeletonItem`, the full-page `Logo
 * variant="loading"` spinner, and Company's `LoadingSkeleton`).
 *
 * - `page`: the mark plus a `Body1` line, for a full route's initial load.
 * - `panel`: a `Skeleton` sized to the panel. Once the panel has been
 *   loading for more than 15s, a `Caption1` "Still working…" line appears
 *   underneath so a slow request doesn't look stuck. `layout` shapes the
 *   skeleton like the content it stands in for (2026-09-20, after the
 *   dashboard's flat grey slabs were called out): `kpi` is a label line, a
 *   headline value and a 2x2 sub-stat grid; `metrics` is two labelled
 *   columns of short rows; `chart` is a row of bars at varying heights;
 *   `table` is a header row and five body rows; `block` is the original
 *   single rectangle.
 */
const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalM,
    minHeight: '50vh',
    width: '100%',
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    width: '100%',
  },
  stillWorking: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalS,
  },
  metricsCols: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalS,
  },
  tableRows: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    width: '100%',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 2fr 2fr 1fr',
    columnGap: tokens.spacingHorizontalXL,
    alignItems: 'center',
  },
  chartBars: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
    width: '100%',
  },
});

export type LoadingStateLayout = 'block' | 'kpi' | 'metrics' | 'chart' | 'table';

// Fixed, deliberately irregular bar heights (percent of the chart area) so
// the placeholder reads as "a chart" rather than a pattern.
const CHART_BAR_HEIGHTS = [38, 62, 48, 80, 56, 70, 44, 66, 52, 74, 40, 60];
const METRIC_ROW_WIDTHS = ['70%', '55%', '64%', '48%'];

export interface LoadingStateProps {
  variant: 'page' | 'panel';
  /** Page variant only. Defaults to a generic "Loading…" line. */
  label?: string;
  /** Panel variant only. Skeleton height, default 200. */
  height?: number | string;
  /** Panel variant only. Shape of the placeholder; default `block`. */
  layout?: LoadingStateLayout;
  className?: string;
}

const STILL_WORKING_DELAY_MS = 15000;

function PanelSkeleton({ layout, height }: { layout: LoadingStateLayout; height: number | string }) {
  const styles = useStyles();
  if (layout === 'kpi') {
    return (
      <Skeleton aria-label="Loading">
        <SkeletonItem shape="rectangle" size={12} style={{ width: '38%' }} />
        <SkeletonItem shape="rectangle" size={40} style={{ width: '52%', marginTop: tokens.spacingVerticalS }} />
        <SkeletonItem shape="rectangle" size={12} style={{ width: '60%', marginTop: tokens.spacingVerticalXS }} />
        <div className={styles.kpiGrid}>
          {[0, 1, 2, 3].map((i) => (
            <SkeletonItem key={i} shape="rectangle" size={16} style={{ width: i % 2 ? '72%' : '84%' }} />
          ))}
        </div>
      </Skeleton>
    );
  }
  if (layout === 'metrics') {
    return (
      <Skeleton aria-label="Loading">
        <div className={styles.metricsCols}>
          {[0, 1].map((col) => (
            <div key={col} className={styles.panel}>
              <SkeletonItem shape="rectangle" size={12} style={{ width: '45%' }} />
              {METRIC_ROW_WIDTHS.map((w, i) => (
                <SkeletonItem key={i} shape="rectangle" size={16} style={{ width: w }} />
              ))}
            </div>
          ))}
        </div>
      </Skeleton>
    );
  }
  if (layout === 'table') {
    return (
      <Skeleton aria-label="Loading">
        <div className={styles.tableRows}>
          {[0, 1, 2, 3, 4, 5].map((row) => (
            <div key={row} className={styles.tableRow}>
              {['55%', '40%', '40%', '30%'].map((w, col) => (
                <SkeletonItem key={col} shape="rectangle" size={row === 0 ? 12 : 16} style={{ width: w }} />
              ))}
            </div>
          ))}
        </div>
      </Skeleton>
    );
  }
  if (layout === 'chart') {
    const areaHeight = typeof height === 'number' ? height : 240;
    return (
      <Skeleton aria-label="Loading">
        <div className={styles.chartBars} style={{ height: areaHeight }}>
          {CHART_BAR_HEIGHTS.map((pct, i) => (
            <SkeletonItem key={i} shape="rectangle" style={{ flex: 1, height: `${pct}%` }} />
          ))}
        </div>
      </Skeleton>
    );
  }
  return (
    <Skeleton aria-label="Loading">
      <SkeletonItem shape="rectangle" style={{ width: '100%', height }} />
    </Skeleton>
  );
}

export function LoadingState({ variant, label, height = 200, layout = 'block', className }: LoadingStateProps) {
  const styles = useStyles();
  const { themeName } = useAuth();
  const logoTheme = themeName === 'dark' || themeName === 'contrast' ? 'dark' : 'light';
  const [showStillWorking, setShowStillWorking] = useState(false);

  useEffect(() => {
    if (variant !== 'panel') {
      return;
    }
    setShowStillWorking(false);
    const timer = window.setTimeout(() => setShowStillWorking(true), STILL_WORKING_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [variant]);

  if (variant === 'page') {
    return (
      <div className={className ? `${styles.page} ${className}` : styles.page}>
        <Logo variant="loading" theme={logoTheme} height={72} />
        <Body1>{label ?? 'Loading…'}</Body1>
      </div>
    );
  }

  return (
    <div className={className ? `${styles.panel} ${className}` : styles.panel}>
      <PanelSkeleton layout={layout} height={height} />
      {showStillWorking && <Caption1 className={styles.stillWorking}>Still working…</Caption1>}
    </div>
  );
}
